<template>
    <k-panel class="border">
        <k-panel-header slot="header">
            <div slot="left">Second Factor Authentication</div>
        </k-panel-header>

        <div v-if="$root.$data.investorProfile.secondFactorAuth === null">
            <div class="text-sm mb-4">
                For your account security we strongly recommend you to enable second factor for your authentication.
            </div>
            <div class="text-sm mb-4">
                You can choose between two:
                <ul>
                    <li>- receive SMS message with login code.</li>
                    <li>- use authenticator app on your mobile device to generate one-time password for login.</li>
                </ul>
                <a-button type="primary" class="mt-4" @click="setupSms">Setup SMS</a-button>
                <a-button type="primary" class="ml-4 mt-4 mb-8" @click="setupTotp">Setup authenticator app</a-button>
            </div>
        </div>

        <div v-if="$root.$data.investorProfile.secondFactorAuth === 'SMS'">
            <div class="text-sm mb-4">
                You are using <b>SMS</b> services to receive one time passwords for your account security.
            </div>
            <div class="text-sm mb-4">
                <div>
                    Alternatively, you can use authenticator app on your mobile device to generate time based one time
                    passwords. <br>
                    <a-button v-if="!showTotpForm" type="primary" class="mt-4" @click="setupTotp">Switch to TOTP</a-button>
                </div>
            </div>
        </div>

        <div v-if="showTotpForm" class="text-sm mt-6">
            <div class="mb-4">
                <b>1.</b> Install the
                <a href="https://googleauthenticator.net/" target="_blank">Google Authenticator</a>
                app on your mobile
                <a class="underline"
                   href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                   target="_blank">Android</a>
                or
                <a class="underline" href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                   target="_blank">iOS</a>
                device if you don't have one.
            </div>
            <div class="mb-4">
                <b>2.</b> Scan the code with the Authenticator app.
                <k-panel-row>
                    <div slot="left">
                        <a :href="totpSetupInfo.registrationURL">
                            <qrcode v-if="totpSetupInfo.registrationURL"
                                    :options="{ width: 200}"
                                    :value="decodeURI(totpSetupInfo.registrationURL)"></qrcode>
                        </a>
                    </div>
                    <div slot="right" class="text-sm">
                        <div class="mb-2">Authentication secret code: <b>{{ totpSetupInfo.secret }}</b></div>
                        <div class="mb-2">Please write down or print a copy of secret code and put in a safe place.
                            If
                            your phone
                            gets lost, stolen or erased, you will need this code to setup Authenticator app.
                        </div>
                        <div class="mb-2">Do not share this code with anyone.</div>
                    </div>
                </k-panel-row>
            </div>
            <div class="mb-4">
                <b>3.</b> Enter the Two-factor authentication code provided by the Authenticator app.
            </div>

            <form autocomplete="off">
                <k-input
                    class="mb-4"
                    id="totp"
                    label="Token"
                    v-model="confirmTotpForm.totpToken"
                    :field-errors="validation.fieldErrors"/>

                <k-button @click.native.prevent="confirmTotpSetup">Confirm</k-button>
            </form>
        </div>

        <div v-if="$root.$data.investorProfile.secondFactorAuth === 'TOTP'">
            <div class="text-sm mb-4">
                You are using <b>Authenticator app</b> to generate one time passwords for your account security.
            </div>
            <div class="text-sm mb-4">
                <div>
                    Alternatively, you can setup sending of authentication tokens using SMS services. <br>
                    <a-button v-if="!showSmsForm" type="primary" class="mt-4" @click="setupSms">Switch to SMS</a-button>
                </div>
            </div>
        </div>

        <div v-if="showSmsForm" class="text-sm">
            We have sent an SMS with one time password to your mobile number <b>{{smsSetupInfo.phoneNumber}}</b>. Please confirm changes by
            entering received code from SMS.
            <form autocomplete="off" class="mt-4">
                <k-input
                    class="mb-4"
                    id="code"
                    label="SMS Code"
                    v-model="confirmSmsForm.smsToken"
                    :field-errors="validation.fieldErrors"/>
                <div class="mt-5 text-gray-500 text-sm" v-if="smsSetupInfo.code">
                    {{smsSetupInfo.code}}
                </div>
                <k-button @click.native.prevent="confirmSmsSetup">Confirm</k-button>
            </form>
        </div>

    </k-panel>
</template>

<script>
export default {
    data () {
        return {
            validation: {},
            showTotpForm: false,
            showSmsForm: false,
            totpSetupInfo: {},
            smsSetupInfo: {},
            confirmTotpForm: {
                totpToken: null
            },
            confirmSmsForm: {
                smsToken: null
            }
        }
    },
    methods: {
        async refresh() {
            this.validation = {
                fieldErrors: {}
            }
            this.showTotpForm = false
            this.showSmsForm = false
            this.totpSetupInfo = {}
            this.smsSetupInfo = {}
            await this.$fetchInvestorProfile();
        },
        async setupSms () {
            this.smsSetupInfo = await this.$post('setup-sms')
            this.showSmsForm = true;
            this.showTotpForm = false;
            this.$trackGAEvent('authentication', 'authenticationTwoFactorStart');
        },
        async confirmSmsSetup () {
            this.validation = {
                fieldErrors: {}
            }
            try {
                await this.$post('enable-sms', this.confirmSmsForm)
                await this.refresh();
                this.$message.success("Successfully enabled SMS")
                this.$trackGAEvent('authentication', 'authenticationTwoFactorConfirm');
            } catch (error) {
                if (error.response && error.response.status === 400) {
                    this.validation = error.response.data
                    this.$scrollToErrorField()
                }
            }
        },
        async setupTotp () {
            this.totpSetupInfo = await this.$post('setup-totp')
            this.showSmsForm = false
            this.showTotpForm = true
        },
        async confirmTotpSetup () {
            this.validation = {
                fieldErrors: {}
            }
            try {
                await this.$post('enable-totp', this.confirmTotpForm)
                await this.refresh();
                this.$message.success("Successfully enabled TOTP")
            } catch (error) {
                if (error.response && error.response.status === 400) {
                    this.validation = error.response.data
                    this.$scrollToErrorField()
                }
            }
        },
    }
}
</script>


